import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface AccordionItemProps {
  title: string;
  content: string;
  isOpen: boolean;
  onClick: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, content, isOpen, onClick }) => {
  return (
    <div className="mb-4 last:mb-0 sm:mb-6">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-3 sm:p-5 text-left bg-[#234152] hover:bg-[#36637d] rounded-t-lg text-white transition-colors duration-200"
      >
        <span className="font-medium text-xs sm:text-sm md:text-base">{title}</span>
        <ChevronDown 
          className={`w-4 h-4 sm:w-5 sm:h-5 transform transition-transform duration-300 flex-shrink-0 ml-2 sm:ml-4 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out bg-white rounded-b-lg ${
          isOpen ? 'border-x border-b border-blue-900' : ''
        }`}
        style={{
          maxHeight: isOpen ? '500px' : '0',
          opacity: isOpen ? 1 : 0
        }}
      >
        <div className="p-3 sm:p-5 text-xs sm:text-sm md:text-base text-gray-700">
          {content}
        </div>
      </div>
    </div>
  );
};

interface AccordionData {
  title: string;
  content: string;
}

const FaqAboutService: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const accordionData: AccordionData[] = [
    {
      title: "What services do you offer?",
      content: "We provide assignment writing, editing, and research assistance for students across various academic levels."
    },
    {
      title: "Are your assignments plagiarism-free?",
      content: "Yes, all assignments are 100% original, written from scratch, and verified through plagiarism-detection tools."
    },
    {
      title: "Who can use your services?",
      content: "Our services are available to students, researchers, and professionals seeking academic or assignment writing support."
    },
    {
      title: "What is the delivery time for assignments?",
      content: "Delivery time depends on the complexity and length of the assignment. We ensure timely delivery for all orders."
    },
    {
      title: "How experienced are your writers?",
      content: "Our team consists of highly qualified professionals with expertise in various academic fields and years of writing experience."
    }
  ];

  return (
    <section className="w-full bg-[#f3f4f6] pt-[17px]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-blue-950">
            Frequently Asked{" "}
          <span
            style={{
              background:
                "linear-gradient(-96deg, rgb(255 255 255 / 0%) 0%, rgb(255 249 220 / 0%) 28%, rgb(253, 103, 16) 100%, rgb(255 255 255 / 0%) 100%)",
              padding: "0px 5px",
            }}
          >
            Questions About Our
          </span>
          Assignment Writing Services
          </h1>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 lg:gap-16 items-stretch">
          {/* Left Column - FAQ */}
          <div className="w-full lg:w-1/2 flex flex-col">
            <div className="h-full flex flex-col justify-between">
              {accordionData.map((item, index) => (
                <AccordionItem
                  key={index}
                  title={item.title}
                  content={item.content}
                  isOpen={openIndex === index}
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                />
              ))}
            </div>
          </div>

          {/* Right Column - Images */}
          <div className="w-full lg:w-1/2 flex items-center mt-6 lg:mt-0">
            <div className="relative w-full border-2 border-[#234152] rounded-xl p-4 sm:p-6 md:p-8">
              {/* Image Grid */}
              <div className="grid grid-cols-2 gap-3 sm:gap-6 md:gap-8 relative z-10">
                <div className="aspect-square rounded-tr-[30px] sm:rounded-tr-[60px] md:rounded-tr-[80px] overflow-hidden shadow-xl">
                  <img
                    src="1731561790087.jpg"
                    alt="Student studying"
                    className="w-full h-full object-fit transform hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="aspect-square mt-4 sm:mt-8 md:mt-12 rounded-bl-[30px] sm:rounded-bl-[60px] md:rounded-bl-[80px] overflow-hidden shadow-xl">
                  <img
                    src="1731560790962.jpg"
                    alt="Library study"
                    className="w-full h-full object-fit transform hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaqAboutService;